import { useEffect, useRef, useState } from 'preact/hooks';
import type { Rating } from '../../shared/types.ts';
import { language } from '../../shared/languages.ts';
import { useModel, type Model } from '../model.ts';
import { pickNext, previewIntervals, formatInterval, State, type CardInfo } from '../srs.ts';
import { playNote, playBlob, stop, warm, clipsFor, canRecord, Recorder, voiceLabel } from '../audio.ts';
import { Reading } from '../Reading.tsx';

const RATINGS: { r: Rating; label: string; cls: string }[] = [
  { r: 1, label: 'Again', cls: 'again' },
  { r: 2, label: 'Hard', cls: 'hard' },
  { r: 3, label: 'Good', cls: 'good' },
  { r: 4, label: 'Easy', cls: 'easy' },
];

function nextCard(m: Model): CardInfo | null {
  return pickNext(m.queue(), m.lastCardId);
}

/** Decode the queue's next few cards' audio now, so it starts the moment they're shown. */
function warmUpcoming(m: Model): void {
  const q = m.queue();
  for (const c of [...q.learning, ...q.review.slice(0, 3), ...q.fresh.slice(0, 3)]) {
    const n = m.notes.get(c.noteId);
    if (n) warm(clipsFor(n));
  }
}

export function Review() {
  const m = useModel();
  const [card, setCard] = useState<CardInfo | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [clip, setClip] = useState<string | null>(null);
  const [mine, setMine] = useState<Blob | null>(null);
  const [recording, setRecording] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [, tick] = useState(0);
  const shownAt = useRef(Date.now());
  const recorder = useRef<Recorder | null>(null);

  const note = card ? m.notes.get(card.noteId) : undefined;
  const deck = note ? m.deckOf(note) : undefined;
  const lang = language(deck?.language ?? 'none');
  const audioDeck = lang.voices.length > 0;

  /** Whether a card is studied by ear: its deck has voices, or it has your recordings. */
  function byEar(c: CardInfo): boolean {
    const n = m.notes.get(c.noteId);
    return !!n && (language(m.deckOf(n)?.language ?? 'none').voices.length > 0 || n.recordings.length > 0);
  }

  function show(c: CardInfo | null, autoplay: boolean) {
    stop();
    recorder.current?.cancel();
    recorder.current = null;
    setRecording(false);
    setCard(c);
    setRevealed(false);
    setMine(null);
    setMicError(null);
    shownAt.current = Date.now();
    const n = c && m.notes.get(c.noteId);
    if (c && n && autoplay && m.settings.autoplay && c.kind === 'recognize' && byEar(c)) {
      void playNote(n, language(m.deckOf(n)?.language ?? 'none').speech).then(setClip);
    } else {
      setClip(null);
    }
    warmUpcoming(m);
  }

  // Pick the first card once the model is loaded, and again whenever there
  // isn't one (a sync brought new cards, or a learning step came due).
  useEffect(() => {
    if (!m.ready || card) return;
    const c = nextCard(m);
    if (c) show(c, false);
  }, [m.ready, m.version, card]);

  // A card that vanished under us (deleted elsewhere, or out of the chosen deck) is dropped.
  useEffect(() => {
    if (!card) return;
    const studying = m.studyDeck();
    if (!note || note.deleted || !deck || deck.deleted || !deck.kinds[card.kind] || (studying && studying !== deck.id)) {
      show(nextCard(m), false);
    }
  }, [m.version]);

  // While idle, look for learning cards coming due.
  useEffect(() => {
    if (card) return;
    const t = setInterval(() => tick((x) => x + 1), 15_000);
    return () => clearInterval(t);
  }, [card]);

  function play(rate = 1, sameClip = true) {
    if (!note) return;
    void playNote(note, lang.speech, { rate, clip: sameClip ? clip : null }).then((c) => c && setClip(c));
  }

  function otherVoice() {
    if (!note) return;
    const others = clipsFor(note).filter((c) => c !== clip);
    const next = others.length ? others[Math.floor(Math.random() * others.length)] : clip;
    void playNote(note, lang.speech, { clip: next }).then((c) => c && setClip(c));
  }

  function reveal() {
    if (!card || !note || revealed) return;
    if (recording) void stopRecording();
    setRevealed(true);
    if (card.kind === 'produce' && m.settings.autoplay && byEar(card)) play();
  }

  function rate(r: Rating) {
    if (!card || !revealed) return;
    m.answer(card, r, Date.now() - shownAt.current);
    show(nextCard(m), true);
  }

  async function undo() {
    const c = await m.undo();
    if (c) show(c, true);
  }

  async function toggleRecording() {
    if (recording) return stopRecording();
    setMicError(null);
    try {
      stop();
      const rec = new Recorder();
      await rec.start();
      recorder.current = rec;
      setRecording(true);
    } catch (e) {
      setMicError(e instanceof Error ? e.message : String(e));
    }
  }

  async function stopRecording() {
    const rec = recorder.current;
    recorder.current = null;
    setRecording(false);
    if (rec) setMine(await rec.stop());
  }

  async function compare() {
    if (!note || !mine) return;
    await playNote(note, lang.speech, { clip });
    await new Promise((r) => setTimeout(r, 350));
    await playBlob(mine);
  }

  // Keyboard: space/enter reveal then Good, 1-4 rate, r replay, s slow, v voice, m mic, z undo.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === ' ' || k === 'enter') {
        e.preventDefault();
        if (!revealed) reveal();
        else rate(3);
      } else if (revealed && ['1', '2', '3', '4'].includes(k)) rate(Number(k) as Rating);
      else if (k === 'r') play();
      else if (k === 's') play(m.settings.slowRate);
      else if (k === 'v') otherVoice();
      else if (k === 'm' && card?.kind === 'produce' && audioDeck) void toggleRecording();
      else if (k === 'z') void undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!m.ready) return <div class="center muted">Loading…</div>;

  const decks = m.activeDecks();
  const studying = m.studyDeck();
  const q = m.queue();
  const header = (
    <>
      {decks.length > 1 && (
        <div class="chips">
          {[null, ...decks.map((d) => d.id)].map((id) => {
            const dq = m.queue(id);
            const due = dq.fresh.length + dq.learning.length + dq.review.length;
            return (
              <button
                key={id ?? 'all'}
                class={`chip ${studying === id ? 'on' : ''}`}
                onClick={() => {
                  void m.saveSettings({ studyDeck: id });
                  setCard(null);
                }}
              >
                {id ? m.decks.get(id)!.name : 'All decks'} <span class="muted">{due}</span>
              </button>
            );
          })}
        </div>
      )}
      <div class="counts">
        <span class="count new">
          {q.fresh.length} <small>new</small>
        </span>
        <span class="count learn">
          {q.learning.length + q.learnAhead.length} <small>learning</small>
        </span>
        <span class="count due">
          {q.review.length} <small>due</small>
        </span>
        {m.lastAnswer && (
          <button class="link undo" onClick={undo}>
            Undo
          </button>
        )}
      </div>
    </>
  );

  if (!card || !note || !deck) {
    return (
      <div class="review">
        {header}
        <Done m={m} onMore={() => show(nextCard(m), false)} />
      </div>
    );
  }

  const ear = byEar(card);
  const clips = clipsFor(note);
  // An audio card with nothing to play yet falls back to showing the words.
  const listenOnly = ear && clips.length > 0;
  const isNew = card.fsrs.state === State.New;
  const intervals = revealed ? previewIntervals(m.scheduler, card.fsrs, new Date()) : null;
  const voice = clip && note.tts.find((t) => t.hash === clip)?.voice;
  const tones = lang.readingStyle === 'tones';
  const showText = !ear || m.settings.showText || !note.reading;

  const audioButtons = (
    <div class="audio-row">
      <button class="play big" onClick={() => play()} aria-label="Play">
        ▶
      </button>
      <button class="play" onClick={() => play(m.settings.slowRate)} aria-label="Play slowly">
        {m.settings.slowRate}×
      </button>
      {clips.length > 1 && (
        <button class="play" onClick={otherVoice} aria-label="Another voice">
          ↻
        </button>
      )}
    </div>
  );

  const theWords = (
    <>
      {note.reading && (
        <p>
          <Reading text={note.reading} tones={tones} class="big" />
        </p>
      )}
      {showText && note.text && <p class={ear ? 'text' : 'text big'}>{note.text}</p>}
    </>
  );

  const label = card.kind === 'recognize' ? (ear ? 'Listen' : 'Recognize') : ear ? 'Say it' : 'Recall';
  return (
    <div class="review">
      {header}
      <div class={`card ${card.kind}`}>
        <div class="card-tag">
          {label}
          {isNew && <span class="badge">new</span>}
          {decks.length > 1 && !studying && <span class="muted"> · {deck.name}</span>}
        </div>

        {card.kind === 'recognize' ? (
          <div class="front">
            {listenOnly ? (
              audioButtons
            ) : (
              <>
                {note.text && <p class="text big">{note.text}</p>}
                {ear && note.reading && <Reading text={note.reading} tones={tones} class="big" />}
                {ear && <p class="muted small">No audio on this device yet.</p>}
              </>
            )}
            {!revealed && <p class="prompt">What does it mean?</p>}
          </div>
        ) : (
          <div class="front">
            <p class="meaning big">{note.meaning}</p>
            {!revealed && ear && canRecord() && (
              <button class={`mic ${recording ? 'on' : ''}`} onClick={toggleRecording}>
                {recording ? '■ Stop' : mine ? '● Record again' : '● Record yourself'}
              </button>
            )}
            {!revealed && mine && !recording && <p class="muted small">Recorded. Reveal to compare.</p>}
            {micError && <p class="error small">{micError}</p>}
          </div>
        )}

        {revealed && (
          <div class="back">
            {card.kind === 'recognize' ? (
              <>
                <p class="meaning big">{note.meaning}</p>
                {listenOnly && theWords}
                {!listenOnly && !ear && note.reading && <Reading text={note.reading} tones={tones} />}
              </>
            ) : (
              <>
                {theWords}
                {ear && audioButtons}
                {mine && (
                  <div class="audio-row">
                    <button onClick={() => playBlob(mine)}>▶ Mine</button>
                    <button onClick={compare}>⇄ Compare</button>
                  </div>
                )}
              </>
            )}
            {note.notes && <p class="notes">{note.notes}</p>}
            {voice && <p class="muted small">Voice: {voiceLabel(voice)}</p>}
          </div>
        )}
      </div>

      <div class="answer-bar">
        {!revealed ? (
          <button class="reveal" onClick={reveal}>
            Show answer
          </button>
        ) : (
          RATINGS.map(({ r, label, cls }) => (
            <button key={r} class={`rate ${cls}`} onClick={() => rate(r)}>
              <span>{label}</span>
              <small>{intervals![r]}</small>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function Done({ m, onMore }: { m: Model; onMore: () => void }) {
  const q = m.queue();
  const studying = m.studyDeck();
  const notes = m
    .liveNotes()
    .filter((n) => m.deckOf(n)?.active !== false && (!studying || n.deckId === studying));
  const anyNewLeft = notes.some((n) => {
    const d = m.deckOf(n)!;
    return (['recognize', 'produce'] as const).some(
      (k) => d.kinds[k] && m.cards.get(`${n.id}:${k}`)?.fsrs.state === State.New,
    );
  });
  if (!notes.length) {
    return (
      <div class="done">
        <h2>No cards yet</h2>
        <p class="muted">
          {m.sync.status === 'syncing' ? 'Downloading your decks…' : 'Sync to download your decks, or add cards under Cards.'}
        </p>
      </div>
    );
  }
  return (
    <div class="done">
      <h2>Done for now</h2>
      <p class="muted">
        {q.doneToday} card{q.doneToday === 1 ? '' : 's'} studied today.
        {q.nextLearningAt && ` A learning card comes back in ${formatInterval(q.nextLearningAt - Date.now())}.`}
      </p>
      {anyNewLeft && (
        <button
          onClick={async () => {
            await m.learnMore(5);
            onMore();
          }}
        >
          Learn 5 more new cards
        </button>
      )}
    </div>
  );
}
